import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLicenses, createLicense } from '../api/admin';

export function LicensesPage() {
    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ['licenses'],
        queryFn: () => fetchLicenses(),
    });

    const createMutation = useMutation({
        mutationFn: createLicense,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
        },
    });

    const handleCreateLicense = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        createMutation.mutate({
            productId: formData.get('productId') as string,
            serverLimit: parseInt(formData.get('serverLimit') as string) || 1,
            gracePeriodDays: parseInt(formData.get('gracePeriodDays') as string) || 14,
        });
    };

    if (isLoading) {
        return <div className="text-center py-8">Loading licenses...</div>;
    }

    const licenses = data?.licenses || [];

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Licenses
            </h1>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Generate License
                </h2>
                <form onSubmit={handleCreateLicense} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Product ID
                            </label>
                            <input
                                name="productId"
                                required
                                placeholder="e.g., iptv-control-panel"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Server Limit
                            </label>
                            <input
                                name="serverLimit"
                                type="number"
                                defaultValue="1"
                                min="1"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Grace Period (Days)
                            </label>
                            <input
                                name="gracePeriodDays"
                                type="number"
                                defaultValue="14"
                                min="0"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    >
                        Generate License
                    </button>
                </form>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        License List
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    License Key
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Product
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Server Limit
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Expires
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {licenses.map((license: any) => (
                                <tr key={license.id}>
                                    <td className="px-6 py-4 text-sm font-mono text-gray-900 dark:text-gray-200">
                                        {license.licenseKey}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                        {license.productId}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                        {license.serverLimit}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 text-xs rounded-full ${license.status === 'active'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                            }`}>
                                            {license.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                        {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}